import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "../../Components/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { FaAngleLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import logo from "../../../src/Images/logo_only.png";
import ButtonDropDown from "../../Components/buttonDropDown";
import { RESET_ALL_STATES } from "../../Redux/actions/actions";
import { resendThunk } from "../../Redux/thunks/resendThunk";
import { IoMdArrowBack } from "react-icons/io";
import background from "../../assets/background/background.jpg";
import NavBar from "../../Components/Nav/newNavBar";
import NotificationModal from "../../Components/NotificationModal";
import Footer from "../../Components/Footer/footer";
import { Loader } from "lucide-react";

const Resend = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isLoading, error, data } = useSelector((state) => state.smsResend);
    const { t } = useTranslation();
    const [regID, setRegID] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [showError, setShowError] = useState(false);
    const [triggerSubmit, setTriggerSubmit] = useState(false);
    const [inputType, setInputType] = useState("Phone Number");
    const [phoneNumber, setPhoneNumber] = useState("");

    const [openNotificationModal, setOpenNotificationModal] = useState(false);

    useEffect(() => {
        if (error) {
            setOpenNotificationModal(true);
        }
        if (data) {
            setOpenNotificationModal(true);
        }
    }, [error, data]);

    useEffect(() => {
        setOpenNotificationModal(false);
    }, []);

    const handleButtonClick = (event) => {
        event.preventDefault();

        // Check if the input field is empty
        if (
            (inputType === "RID" && regID.trim() === "") ||
            (inputType === "Phone Number" && phoneNumber.trim() === "")
        ) {
            setShowError("Input field cannot be empty.");
            return;
        }

        dispatch({ type: RESET_ALL_STATES });
        setShowError("");
        setTriggerSubmit(true);
    };

    const handleSubmit = async () => {
        if (inputType === "RID") {
            dispatch(resendThunk(regID));
        } else {
            dispatch(resendThunk(phoneNumber));
        }
        setShowError(error);
        setSubmitDisabled(false);
    };

    useEffect(() => {
        if (triggerSubmit) {
            handleSubmit();
            setTriggerSubmit(false);
        }
    }, [triggerSubmit]);

    const handleChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        const isValid =
            value === "" ||
            (/^(?:09|07)\d{0,8}$/.test(value) && value.length <= 10);

        if (inputType === "RID") {
            setRegID(value);
            setSubmitDisabled(value === "");
        } else if (inputType === "Phone Number" && (isValid || value === "0")) {
            setPhoneNumber(value);
            setSubmitDisabled(value === "");
        }
    };

    const handleInputTypeChange = (e) => {
        const value = e.target.value;
        setInputType(value);
        if (value === "Phone Number") {
            setRegID("");
        } else {
            setPhoneNumber("");
        }
    };

    const identifierLength = () => {
        if (inputType === "RID") {
            return 29;
        } else {
            return 10;
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
        <div className='relative flex flex-col min-h-[100dvh] md:min-h-[100dvh] overflow-hidden bg-cover bg-fin-verification'>
            <NavBar />

            <div className='flex-grow flex flex-col overflow-y-scroll scrollbar-hide py-32 md:min-h-screen md:pb-[6%] items-center justify-center bg-cover bg-opacity-6 font-barlow px-5'>
                <form
                    onSubmit={handleButtonClick}
                    className='w-full max-w-[500px] md:min-w-[600px] text-center shadow-lg rounded-2xl overflow-hidden'
                >
                    <div className='bg-[#184C61] p-3 text-white flex items-center justify-between'>
                        <Link to='/'>
                            <FaAngleLeft size={24} />
                        </Link>{" "}
                        <h2 className='text-base md:text-2xl font-semibold'>
                            {t("resend-sms")}
                        </h2>
                        <div></div>
                    </div>
                    <div className='p-5 flex flex-col gap-2 text-MetallicBlue bg-faydagrey'>
                        <h2 className='text-sm md:text-xl'>
                            {t("enter-rid-or-phone")}
                        </h2>
                        <div className='flex items-center w-full pb-2 md:pb-0 b-yellow-300'>
                            <div className='flex justify-center w-full'>
                                <input
                                    type='text'
                                    name='input'
                                    id='input'
                                    value={
                                        inputType === "RID"
                                            ? regID
                                            : phoneNumber
                                    }
                                    onChange={(e) => handleChange(e)}
                                    placeholder={
                                        inputType === "RID"
                                            ? t("rid-placeholder")
                                            : t("phone-placeholder")
                                    }
                                    minLength={identifierLength()}
                                    maxLength={29}
                                    autoComplete='off'
                                    inputMode='numeric'
                                    className='block w-full px-4 py-1 text-base md:text-lg text-center placeholder-gray-400 border border-gray-300 rounded-md shadow-sm focus:ring-nidblue focus:border-nidblue'
                                />
                            </div>
                        </div>

                        {showError && (
                            <p className='mt-2 text-base md:text-base text-center text-red-500'>
                                {showError}
                            </p>
                        )}

                        <div className='relative flex w-full justify-between mt-1'>
                            <div className='flex w-full items-center justify-center md:pt-3 b-gray-600'>
                                <button
                                    type='submit'
                                    disabled={isLoading || submitDisabled}
                                    className={`
                                        ${
                                            isLoading || submitDisabled
                                                ? "disabled hover:bg-gray-300 hover:text-gray-500 hover:cursor-not-allowed"
                                                : ""
                                        }
                                        w-[35%] md:w-[30%] bg-nidblue md:text-xl text-lg text-white font-bold md:py-2 md:px-4 py-1 rounded-lg hover:scale-95 hover:bg-nidyellow focus:outline-none focus:ring focus:nidgreen focus:ring-opacity-50 transition duration-300 ease-in-out`}
                                >
                                    {isLoading ? (
                                        <div className='flex items-center gap-2 justify-center'>
                                            <Loader className='animate-spin' />
                                            {t("home-button-verifying")}
                                        </div>
                                    ) : (
                                        t("home-button-proceed")
                                    )}
                                </button>
                                {isLoading && <Spinner />}
                            </div>
                            <select
                                value={inputType}
                                onChange={handleInputTypeChange}
                                className='w-[25%] md:w-[30%] absolute md:top-2 right-0 md:py-3 md:px-4 py-1 border border-gray-300 rounded-md'
                            >
                                <option value='Phone Number'>
                                    Phone Number
                                </option>
                                <option value='RID'>RID</option>
                            </select>
                        </div>
                    </div>
                </form>

                {!isLoading ? (
                    <NotificationModal
                        open={openNotificationModal}
                        error={error || data?.status !== "200" ? true : false}
                        title={
                            error || data?.status !== "200"
                                ? "Error"
                                : "Success"
                        }
                        message={
                            error
                                ? error.message
                                : data?.message
                                ? data?.message
                                : data?.error
                        }
                        handleClose={() => setOpenNotificationModal(false)}
                    />
                ) : null}
            </div>
            <Footer />
        </div>
    );
};

export default Resend;
